// ============================================================
// PETFLYCHECK SEO DATA - VERIFIED & SOURCED INFORMATION
// ============================================================
// Sources: IATA Live Animals Regulations (LAR), Official Airline Policies
// Last Updated: 2026
// ============================================================

// ==================== AIRLINES DATA ====================
export const airlinesData = {
  "air-france": {
    slug: "air-france",
    name: "Air France",
    code: "AF",
    country: "France",
    alliance: "SkyTeam",
    logo: "https://upload.wikimedia.org/wikipedia/commons/4/44/Air_France_Logo.svg",
    officialPolicyUrl: "https://www.airfrance.fr/FR/fr/common/transporter/animaux/voyager-animal-avion.htm",
    lastVerified: "2026",
    
    // CABIN RULES
    cabinAllowed: true,
    cabinMaxWeight: 8, // kg including carrier
    cabinCarrierDimensions: "46 x 28 x 24 cm",
    cabinFee: "125-200 EUR",
    cabinNotes: "Soft or rigid carrier that fits under seat. Pet must remain inside during entire flight.",
    
    // CARGO/HOLD RULES  
    cargoAllowed: true,
    cargoMaxWeight: 75, // kg including crate
    cargoMinAge: 10, // weeks
    cargoFee: "75-400 EUR depending on route",
    
    
    // BREED RESTRICTIONS
    restrictedBreeds: {
      dogs: ["American Pit Bull Terrier", "American Staffordshire Terrier", "Staffordshire Bull Terrier", "Tosa", "Boerboel", "Mastiff types"],
      cats: [],
      reason: "French law prohibits transport of Category 1 dangerous dogs"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["Bulldog", "Pug", "Boston Terrier", "Boxer", "Shih Tzu", "Pekingese", "Cavalier King Charles"],
      notes: "Snub-nosed breeds banned from cargo hold due to respiratory risks. Some may travel in cabin if under weight limit.",
      source: "Air France official policy"
    },
    
    // DOCUMENTS REQUIRED
    documentsRequired: [
      "EU Pet Passport (for EU travel)",
      "Rabies vaccination certificate (minimum 21 days before travel)",
      "Health certificate from veterinarian (within 10 days of travel)",
      "Microchip implantation proof",
      "Import permit for certain destinations"
    ],
    
    commonRejectionReasons: [
      "Crate too small - pet cannot stand upright",
      "Missing corner bolts",
      "Soft-sided carrier used for cargo",
      "Expired health certificate",
      "Brachycephalic breed in cargo"
    ],
    
    // SEO CONTENT
    seoTitle: "Air France Pet Travel Policy 2026 | Crate Requirements & Rules",
    seoDescription: "Complete guide to flying with pets on Air France. Cabin & cargo rules, crate requirements, banned breeds, document checklist. Avoid rejection at check-in.",
    seoKeywords: ["Air France pet policy", "Air France dog travel", "Air France cat travel", "Air France pet crate requirements", "Air France banned breeds", "flying with pets Air France"]
  },

  "lufthansa": {
    slug: "lufthansa",
    name: "Lufthansa",
    code: "LH",
    country: "Germany",
    alliance: "Star Alliance",
    officialPolicyUrl: "https://www.lufthansa.com/de/en/travelling-with-animals",
    lastVerified: "2026",
    
    cabinAllowed: true,
    cabinMaxWeight: 8,
    cabinCarrierDimensions: "55 x 40 x 23 cm",
    cabinFee: "50-70 EUR (Europe), 100-150 EUR (Intercontinental)",
    cabinNotes: "Pet must fit in carrier under seat. Carrier counts as hand luggage.",
    
    cargoAllowed: true,
    cargoMaxWeight: 110,
    cargoMinAge: 12,
    cargoFee: "100-600 EUR based on weight and route",
    
    
    restrictedBreeds: {
      dogs: ["Pit Bull Terrier", "American Staffordshire Terrier", "Staffordshire Bull Terrier", "Bull Terrier"],
      cats: [],
      reason: "German dangerous dog regulations"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["Affenpinscher", "Boston Terrier", "Boxer", "Brussels Griffon", "Bulldog (all types)", "Cane Corso", "Dogue de Bordeaux", "Japanese Chin", "Lhasa Apso", "Mastiff (all types)", "Pekingese", "Pug", "Shar-Pei", "Shih Tzu", "Tibetan Spaniel"],
      notes: "Banned from cargo hold year-round. Cabin only for qualifying pets.",
      source: "Lufthansa Animal Lounge policy"
    },
    
    documentsRequired: [
      "EU Pet Passport or equivalent",
      "Rabies vaccination (21+ days old)",
      "Veterinary health certificate",
      "Microchip documentation",
      "Import permits as required by destination"
    ],
    
    commonRejectionReasons: [
      "Crate dimensions incorrect",
      "Ventilation insufficient",
      "Plastic clips instead of metal bolts",
      "Brachycephalic breed booked in cargo"
    ],
    
    seoTitle: "Lufthansa Pet Travel Policy 2026 | Complete Crate & Document Guide",
    seoDescription: "Everything you need to fly with your pet on Lufthansa. Crate specifications, weight limits, banned breeds, required documents. Don't get rejected.",
    seoKeywords: ["Lufthansa pet policy", "Lufthansa dog travel", "Lufthansa pet crate", "Lufthansa animal transport", "flying with dog Lufthansa"]
  },

  "delta": {
    slug: "delta",
    name: "Delta Air Lines",
    code: "DL",
    country: "United States",
    alliance: "SkyTeam",
    officialPolicyUrl: "https://www.delta.com/us/en/pet-travel/overview",
    lastVerified: "2026",
    
    cabinAllowed: true,
    cabinMaxWeight: "No strict limit - must fit under seat",
    cabinCarrierDimensions: "Soft-sided: fits under seat (varies by aircraft)",
    cabinFee: "$95 USD each way (domestic), $200 USD (international)",
    cabinNotes: "One pet per passenger. Carrier counts as carry-on.",
    
    cargoAllowed: true,
    cargoMaxWeight: "300 lbs including crate",
    cargoMinAge: 10,
    cargoFee: "Varies by route - contact Delta Cargo",
    
    
    restrictedBreeds: {
      dogs: ["Pit Bull type dogs"],
      cats: [],
      reason: "Delta policy effective 2018"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["American Bulldog", "American Staffordshire Terrier", "Boston Terrier", "Boxer", "Brussels Griffon", "Bulldog (English)", "Bulldog (French)", "Bull Terrier", "Cane Corso", "Chow Chow", "Dogue de Bordeaux", "English Toy Spaniel", "Japanese Chin", "Lhasa Apso", "Mastiff", "Pekingese", "Pit Bull", "Presa Canario", "Pug", "Shar Pei", "Shih Tzu", "Staffordshire Bull Terrier", "Tibetan Spaniel"],
      notes: "Not accepted in cargo. Must fly in cabin in approved carrier.",
      source: "Delta official snub-nosed breed policy"
    },
    
    temperatureRestrictions: {
      min: 45, // Fahrenheit
      max: 85,
      notes: "No cargo pet travel if ground temp below 45°F or above 85°F at origin, destination, or connection cities"
    },
    
    documentsRequired: [
      "Health certificate within 10 days of travel",
      "Rabies certificate",
      "Import permits for international destinations",
      "USDA endorsement for some countries"
    ],
    
    commonRejectionReasons: [
      "Temperature restrictions (seasonal)",
      "Snub-nosed breed in cargo booking",
      "Crate too small",
      "Missing USDA endorsement for international"
    ],
    
    seoTitle: "Delta Pet Travel Policy 2026 | Cargo, Cabin & Crate Requirements",
    seoDescription: "Complete Delta Air Lines pet policy guide. Cabin fees, cargo rules, banned breeds, temperature restrictions. Prevent airport rejection.",
    seoKeywords: ["Delta pet policy", "Delta dog travel", "Delta pet cargo", "flying with pets Delta", "Delta snub nose policy"]
  },

  "united": {
    slug: "united",
    name: "United Airlines",
    code: "UA",
    country: "United States",
    alliance: "Star Alliance",
    officialPolicyUrl: "https://www.united.com/en/us/fly/travel/animals.html",
    lastVerified: "2026",
    
    cabinAllowed: true,
    cabinMaxWeight: "Must fit in carrier under seat",
    cabinCarrierDimensions: "18 x 11 x 11 inches (hard), varies (soft)",
    cabinFee: "$125 USD each way",
    cabinNotes: "Max 2 pets per carrier if small. Carrier replaces carry-on.",
    
    cargoAllowed: true,
    cargoMaxWeight: "150 lbs max for PetSafe program",
    cargoMinAge: 8,
    cargoFee: "$497-2,158 USD depending on size and route",
    cargoProgram: "United PetSafe",
    
    
    restrictedBreeds: {
      dogs: [],
      cats: [],
      reason: "United does not ban specific breeds but has strict brachycephalic policy"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["Affenpinscher", "American Bully", "American Pit Bull", "American Staffordshire Terrier", "Boston Terrier", "Boxer", "Brussels Griffon", "Bulldog (all)", "Cane Corso", "Chow Chow", "Dogue de Bordeaux", "English Toy Spaniel", "Japanese Chin", "Lhasa Apso", "Mastiff (all)", "Pekingese", "Pit Bull", "Presa Canario", "Pug", "Shar Pei", "Shih Tzu", "Staffordshire Bull Terrier", "Tibetan Spaniel"],
      notes: "PetSafe does not accept snub-nosed dogs or cats. Cabin travel only where applicable.",
      source: "United PetSafe program restrictions"
    },
    
    temperatureRestrictions: {
      min: 45,
      max: 85,
      notes: "Seasonal embargoes apply"
    },
    
    documentsRequired: [
      "Veterinary health certificate (10 days)",
      "Rabies vaccination certificate",
      "Acclimation certificate if applicable",
      "Import permits for destination country"
    ],
    
    commonRejectionReasons: [
      "Plastic clips instead of metal bolts",
      "Crate size non-compliant",
      "Brachycephalic breed",
      "Temperature embargo"
    ],
    
    seoTitle: "United Airlines Pet Policy 2026 | PetSafe Cargo & Cabin Rules",
    seoDescription: "United Airlines pet travel guide. PetSafe program, cabin rules, crate requirements, breed restrictions. Complete checklist to avoid rejection.",
    seoKeywords: ["United pet policy", "United PetSafe", "United dog travel", "United pet cargo cost", "United snub nose breeds"]
  },

  "british-airways": {
    slug: "british-airways",
    name: "British Airways",
    code: "BA",
    country: "United Kingdom",
    alliance: "Oneworld",
    officialPolicyUrl: "https://www.britishairways.com/en-gb/information/travel-assistance/travelling-with-pets",
    lastVerified: "2026",
    
    cabinAllowed: false,
    cabinNotes: "British Airways does NOT allow pets in cabin (except assistance dogs)",
    
    cargoAllowed: true,
    cargoMaxWeight: "Varies by route",
    cargoMinAge: 12,
    cargoProgram: "IAG Cargo",
    cargoFee: "Quote-based via IAG Cargo",
    cargoNotes: "All pets must travel as cargo through IAG Cargo partner",
    
    
    restrictedBreeds: {
      dogs: ["Pit Bull Terrier", "Japanese Tosa", "Dogo Argentino", "Fila Brasileiro"],
      cats: [],
      reason: "UK Dangerous Dogs Act 1991"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["Bulldog", "Pug", "Boxer", "Boston Terrier", "Shih Tzu", "Cavalier King Charles Spaniel", "Pekingese", "Affenpinscher", "Brussels Griffon", "Japanese Chin", "Lhasa Apso"],
      notes: "Snub-nosed breeds face restrictions. Contact IAG Cargo for specific requirements.",
      source: "IAG Cargo live animal policies"
    },
    
    documentsRequired: [
      "Pet Passport or EU Health Certificate",
      "Rabies vaccination (21+ days)",
      "Tapeworm treatment (for UK entry)",
      "Microchip documentation",
      "APHA export certificate"
    ],
    
    commonRejectionReasons: [
      "Incorrect crate specifications",
      "Missing tapeworm treatment",
      "Documentation errors",
      "Breed restrictions"
    ],
    
    seoTitle: "British Airways Pet Travel 2026 | Cargo Only - Complete Guide",
    seoDescription: "British Airways pet policy: cargo-only travel through IAG Cargo. Crate requirements, UK breed restrictions, document checklist. No cabin pets allowed.",
    seoKeywords: ["British Airways pet policy", "BA pet cargo", "British Airways dog travel", "IAG Cargo pets", "UK pet travel rules"]
  },

  "emirates": {
    slug: "emirates",
    name: "Emirates",
    code: "EK",
    country: "United Arab Emirates",
    alliance: "None",
    officialPolicyUrl: "https://www.emirates.com/english/before-you-fly/pets-and-animals/",
    lastVerified: "2026",
    
    cabinAllowed: false,
    cabinNotes: "Emirates does NOT allow pets in passenger cabin (falcons excepted in certain classes)",
    
    cargoAllowed: true,
    cargoMaxWeight: "No specific limit published",
    cargoProgram: "Emirates SkyCargo",
    cargoFee: "Based on volumetric weight",
    cargoNotes: "All pets travel as cargo via Emirates SkyCargo. Booking through cargo department only.",
    
    
    restrictedBreeds: {
      dogs: [],
      cats: [],
      reason: "No specific breed bans but brachycephalic restrictions apply"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["All snub-nosed dogs", "Persian cats", "Himalayan cats", "Exotic Shorthair cats"],
      notes: "Emirates recommends against shipping snub-nosed breeds due to respiratory risks during long-haul flights.",
      source: "Emirates SkyCargo guidelines"
    },
    
    documentsRequired: [
      "Health certificate (issued within 10 days)",
      "Rabies certificate",
      "Import permit for destination country",
      "UAE specific documentation if transiting Dubai"
    ],
    
    commonRejectionReasons: [
      "Missing import permits",
      "Crate non-compliant",
      "Connecting flight issues",
      "Hot weather embargoes"
    ],
    
    seoTitle: "Emirates Pet Travel 2026 | SkyCargo Rules & Requirements",
    seoDescription: "Emirates pet transport via SkyCargo. No cabin pets. Complete cargo requirements, crate specifications, documentation guide. Dubai transit rules.",
    seoKeywords: ["Emirates pet policy", "Emirates dog travel", "Emirates SkyCargo pets", "Dubai pet transport", "Emirates cat travel"]
  },

  "klm": {
    slug: "klm",
    name: "KLM Royal Dutch Airlines",
    code: "KL",
    country: "Netherlands",
    alliance: "SkyTeam",
    officialPolicyUrl: "https://www.klm.com/travel/gb_en/prepare_for_travel/travel_planning/pets/index.htm",
    lastVerified: "2026",
    
    cabinAllowed: true,
    cabinMaxWeight: 8,
    cabinCarrierDimensions: "46 x 28 x 24 cm",
    cabinFee: "EUR 50-200 depending on route",
    
    cargoAllowed: true,
    cargoMaxWeight: 75,
    cargoFee: "EUR 75-400",
    cargoProgram: "KLM Cargo",
    
    
    restrictedBreeds: {
      dogs: ["Pit Bull", "Staffordshire Terrier", "American Bulldog", "Rottweiler mix"],
      cats: [],
      reason: "Dutch dangerous dog regulations"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["Bulldog", "Pug", "Boston Terrier", "Boxer", "Shih Tzu", "Pekingese"],
      notes: "Cargo restrictions apply. Cabin may be possible.",
      source: "KLM official policy"
    },
    
    documentsRequired: [
      "EU Pet Passport",
      "Rabies vaccination",
      "Health certificate",
      "Microchip proof"
    ],
    
    seoTitle: "KLM Pet Travel Policy 2026 | Cabin & Cargo Requirements",
    seoDescription: "KLM pet travel guide: cabin and cargo options, crate specifications, breed restrictions. Amsterdam hub rules and EU pet passport requirements.",
    seoKeywords: ["KLM pet policy", "KLM dog travel", "KLM pet cabin", "Amsterdam pet travel", "KLM animal transport"]
  },

  "american-airlines": {
    slug: "american-airlines",
    name: "American Airlines",
    code: "AA",
    country: "United States",
    alliance: "Oneworld",
    officialPolicyUrl: "https://www.aa.com/i18n/travel-info/special-assistance/pets.jsp",
    lastVerified: "2026",
    
    cabinAllowed: true,
    cabinMaxWeight: "20 lbs including carrier",
    cabinCarrierDimensions: "19 x 13 x 9 inches",
    cabinFee: "$125 USD",
    
    cargoAllowed: true,
    cargoProgram: "American Airlines Cargo",
    cargoMaxWeight: "100 lbs",
    cargoFee: "Varies by route",
    
    
    restrictedBreeds: {
      dogs: ["Brachycephalic breeds - cargo only restrictions"],
      cats: ["Brachycephalic breeds"],
      reason: "Health and safety concerns"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["All snub-nosed dogs and cats"],
      notes: "American Airlines suspended cargo pet transport for brachycephalic breeds.",
      source: "AA official policy"
    },
    
    temperatureRestrictions: {
      min: 45,
      max: 85,
      notes: "No cargo during extreme temperatures"
    },
    
    documentsRequired: [
      "Health certificate (10 days)",
      "Rabies vaccination",
      "Import documents as required"
    ],
    
    seoTitle: "American Airlines Pet Policy 2026 | Cabin & Cargo Guide",
    seoDescription: "American Airlines pet travel rules. Cabin fees, cargo program, breed restrictions, temperature limits. Complete guide to flying with your pet on AA.",
    seoKeywords: ["American Airlines pet policy", "AA pet travel", "American Airlines dog", "AA pet cargo", "American Airlines pet fee"]
  },

  "qantas": {
    slug: "qantas",
    name: "Qantas",
    code: "QF",
    country: "Australia",
    alliance: "Oneworld",
    officialPolicyUrl: "https://www.qantas.com/au/en/travel-info/specific-needs/travelling-with-animals.html",
    lastVerified: "2026",
    
    cabinAllowed: false,
    cabinNotes: "No pets in cabin on Qantas (domestic or international)",
    
    cargoAllowed: true,
    cargoProgram: "Qantas Freight",
    cargoMaxWeight: "Varies",
    cargoFee: "Quote-based",
    
    
    restrictedBreeds: {
      dogs: [],
      cats: [],
      reason: "Australian quarantine applies to all incoming animals"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["All snub-nosed breeds"],
      notes: "Additional restrictions during Australian summer (Oct-Apr)",
      source: "Qantas policy"
    },
    
    specialNotes: "Australia has strict quarantine. All pets entering Australia must complete mandatory quarantine period (minimum 10 days).",
    
    documentsRequired: [
      "Import permit from Australian Department of Agriculture",
      "Rabies certificate",
      "Health certificate",
      "Microchip",
      "Quarantine booking"
    ],
    
    seoTitle: "Qantas Pet Travel 2026 | Australia Cargo & Quarantine Guide",
    seoDescription: "Qantas pet policy and Australian quarantine requirements. Cargo-only travel, crate specifications, import permits. No cabin pets on Qantas.",
    seoKeywords: ["Qantas pet policy", "Australia pet import", "Qantas dog travel", "Australian quarantine pets", "flying pets to Australia"]
  },

  "swiss": {
    slug: "swiss",
    name: "Swiss International Air Lines",
    code: "LX",
    country: "Switzerland",
    alliance: "Star Alliance",
    officialPolicyUrl: "https://www.swiss.com/ch/en/prepare/baggage/animals",
    lastVerified: "2026",
    
    cabinAllowed: true,
    cabinMaxWeight: 8,
    cabinCarrierDimensions: "55 x 40 x 23 cm",
    cabinFee: "CHF 60-200",
    
    cargoAllowed: true,
    cargoMaxWeight: 75,
    cargoFee: "CHF 100-600",
    
    
    restrictedBreeds: {
      dogs: ["Dangerous breed categories per Swiss law"],
      cats: [],
      reason: "Swiss cantonal regulations"
    },
    
    brachycephalicPolicy: {
      restricted: true,
      breeds: ["Bulldog", "Pug", "Boston Terrier", "Boxer", "Pekingese", "Shih Tzu"],
      notes: "Restricted during hot months",
      source: "SWISS policy"
    },
    
    documentsRequired: [
      "EU Pet Passport",
      "Rabies vaccination",
      "Health certificate",
      "Swiss import requirements"
    ],
    
    seoTitle: "SWISS Pet Travel Policy 2026 | Cabin & Cargo Guide",
    seoDescription: "SWISS Air Lines pet policy. Cabin and cargo rules, crate requirements, Swiss import regulations. Flying with pets through Zurich.",
    seoKeywords: ["SWISS pet policy", "Swiss Air pet travel", "Zurich pet transport", "Swiss dog travel", "SWISS animal policy"]
  }
};

// ==================== COUNTRIES DATA ====================
export const countriesData = {
  "united-states": {
    slug: "united-states",
    name: "United States",
    code: "US",
    flag: "🇺🇸",
    
    importRules: {
      quarantine: false,
      quarantineDays: 0,
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "None for most states, varies by state",
      additionalVaccines: [],
      healthCertificate: true,
      healthCertificateValidity: "10 days before travel",
      usdaEndorsement: "Required for most countries returning to US",
      importPermit: false,
      bannedBreeds: ["Varies by state and city"]
    },
    
    specialRequirements: [
      "CDC requires dogs to be healthy and have valid rabies vaccination",
      "Dogs from high-risk rabies countries face additional requirements",
      "Some states (Hawaii) require quarantine",
      "Pit bull bans in some cities (Miami, Denver)"
    ],
    
    entryPoints: ["All international airports"],
    
    commonIssues: [
      "Incomplete CDC documentation",
      "Dogs from high-risk countries without proper serology",
      "State-specific requirements overlooked"
    ],
    
    seoTitle: "USA Pet Import Rules 2026 | Dogs & Cats Entry Requirements",
    seoDescription: "Complete guide to importing pets to USA. CDC requirements, rabies rules, state-specific regulations, USDA endorsement. Avoid customs rejection.",
    seoKeywords: ["USA pet import", "US dog import", "bring pet to America", "USA pet quarantine", "CDC pet requirements"]
  },

  "united-kingdom": {
    slug: "united-kingdom",
    name: "United Kingdom",
    code: "GB",
    flag: "🇬🇧",
    
    importRules: {
      quarantine: false,
      quarantineDays: 0,
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "21 days minimum",
      additionalVaccines: [],
      healthCertificate: true,
      healthCertificateValidity: "10 days",
      tapewormTreatment: true,
      tapewormTiming: "24-120 hours before entry",
      importPermit: false,
      bannedBreeds: ["Pit Bull Terrier", "Japanese Tosa", "Dogo Argentino", "Fila Brasileiro"]
    },
    
    specialRequirements: [
      "Pet must enter through approved routes (carrier/ferry)",
      "Tapeworm treatment mandatory (not from Finland, Ireland, Malta, or Norway)",
      "Microchip must be ISO 15-digit or bring own reader",
      "Must travel with pet or within 5 days",
      "Animal Health Certificate (AHC) required post-Brexit"
    ],
    
    entryPoints: ["Approved carriers and ferry routes only"],
    
    commonIssues: [
      "Tapeworm treatment timing incorrect",
      "Microchip not readable",
      "AHC form errors",
      "Wrong entry route used"
    ],
    
    seoTitle: "UK Pet Import Rules 2026 | Post-Brexit Dog & Cat Entry Guide",
    seoDescription: "Bringing pets to UK post-Brexit. Animal Health Certificate, tapeworm treatment, microchip, rabies rules. Complete entry requirements.",
    seoKeywords: ["UK pet import", "bring dog to UK", "UK pet travel", "UK tapeworm treatment", "UK animal health certificate"]
  },

  "australia": {
    slug: "australia",
    name: "Australia",
    code: "AU",
    flag: "🇦🇺",
    
    importRules: {
      quarantine: true,
      quarantineDays: "Minimum 10 days",
      quarantineFacility: "Mickleham Quarantine Facility",
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "Varies by origin country",
      additionalVaccines: ["Varies by species"],
      healthCertificate: true,
      healthCertificateValidity: "Strict timing requirements",
      importPermit: true,
      permitLeadTime: "Apply 20+ days before export",
      bloodTests: ["Rabies serology (RNATT)"],
      bannedBreeds: ["Dogo Argentino", "Fila Brasileiro", "Japanese Tosa", "American Pit Bull", "Presa Canario"]
    },
    
    approvedCountries: {
      category1: ["New Zealand"],
      category2: ["Norfolk Island", "Cocos Islands"],
      category3: ["Most of Europe, USA, Canada, Japan, Singapore", "6+ month preparation required"]
    },
    
    specialRequirements: [
      "Only dogs and cats from approved countries",
      "Rabies antibody test required",
      "Multiple vet visits with specific timing",
      "Import permit application months in advance",
      "Quarantine booking required",
      "Strict treatment schedule for internal/external parasites"
    ],
    
    entryPoints: ["Sydney, Melbourne (Mickleham) quarantine facility only"],
    
    commonIssues: [
      "Insufficient preparation time",
      "Failed RNATT blood test",
      "Incorrect treatment timing",
      "Import permit delays"
    ],
    
    seoTitle: "Australia Pet Import 2026 | Quarantine, Permit & Requirements",
    seoDescription: "Complete guide to importing pets to Australia. Mandatory quarantine, RNATT test, import permits, approved countries. 6+ months preparation needed.",
    seoKeywords: ["Australia pet import", "Australian quarantine", "bring dog to Australia", "Australia pet permit", "Mickleham quarantine"]
  },

  "france": {
    slug: "france",
    name: "France",
    code: "FR",
    flag: "🇫🇷",
    
    importRules: {
      quarantine: false,
      quarantineDays: 0,
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "21 days",
      additionalVaccines: [],
      healthCertificate: true,
      healthCertificateValidity: "10 days",
      importPermit: false,
      euPetPassport: "Required for EU citizens, AHC for others"
    },
    
    specialRequirements: [
      "EU Pet Passport for EU residents",
      "Animal Health Certificate for non-EU",
      "Microchip before rabies vaccination",
      "Category 1 dogs banned (attack dogs)"
    ],
    
    bannedBreeds: ["Pit Bull (Category 1)", "Boerboel", "Tosa without pedigree"],
    
    entryPoints: ["All airports and borders"],
    
    commonIssues: [
      "Rabies vaccination before microchip",
      "Wrong health certificate form",
      "Category 1 dog import attempt"
    ],
    
    seoTitle: "France Pet Import Rules 2026 | EU Pet Passport & Requirements",
    seoDescription: "Bringing pets to France. EU Pet Passport, rabies requirements, banned breeds, health certificate. Complete import guide.",
    seoKeywords: ["France pet import", "bring dog to France", "EU pet passport France", "France banned dog breeds", "French pet rules"]
  },

  "germany": {
    slug: "germany",
    name: "Germany",
    code: "DE",
    flag: "🇩🇪",
    
    importRules: {
      quarantine: false,
      quarantineDays: 0,
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "21 days",
      additionalVaccines: [],
      healthCertificate: true,
      importPermit: false,
      euPetPassport: "Required for EU, AHC for non-EU"
    },
    
    specialRequirements: [
      "EU Pet Passport or Animal Health Certificate",
      "Dangerous dog regulations vary by state (Bundesland)",
      "Some states require behavior tests for certain breeds",
      "Muzzle requirements in public for some breeds"
    ],
    
    bannedOrRestrictedBreeds: {
      federal: ["Pit Bull Terrier", "American Staffordshire Terrier", "Staffordshire Bull Terrier", "Bull Terrier"],
      notes: "Individual states (Länder) may have additional restrictions"
    },
    
    seoTitle: "Germany Pet Import 2026 | Rules, Banned Breeds & Documents",
    seoDescription: "Importing pets to Germany. EU requirements, dangerous dog laws by state, health certificates. Complete guide for dogs and cats.",
    seoKeywords: ["Germany pet import", "bring dog to Germany", "German dangerous dog law", "Germany pet rules", "EU pet passport Germany"]
  },

  "canada": {
    slug: "canada",
    name: "Canada",
    code: "CA",
    flag: "🇨🇦",
    
    importRules: {
      quarantine: false,
      quarantineDays: 0,
      microchipRequired: false,
      rabiesRequired: true,
      rabiesRequiredAge: "3 months+",
      rabiesWaitingPeriod: "None",
      additionalVaccines: [],
      healthCertificate: "Not required from USA",
      healthCertificateValidity: "Varies by origin",
      importPermit: false
    },
    
    specialRequirements: [
      "Dogs 3+ months need rabies vaccination",
      "Puppies under 3 months: no rabies needed",
      "From USA: only rabies certificate required",
      "Commercial imports: CFIA requirements apply"
    ],
    
    seoTitle: "Canada Pet Import 2026 | Easy Entry Rules for Dogs & Cats",
    seoDescription: "Simple guide to bringing pets to Canada. Rabies vaccination requirements, documentation, no quarantine. Easy import from USA.",
    seoKeywords: ["Canada pet import", "bring dog to Canada", "Canada pet rules", "Canada rabies requirement", "pet travel Canada"]
  },

  "japan": {
    slug: "japan",
    name: "Japan",
    code: "JP",
    flag: "🇯🇵",
    
    importRules: {
      quarantine: "Possible",
      quarantineDays: "Up to 180 days if requirements not met",
      microchipRequired: true,
      microchipType: "ISO 11784/11785",
      rabiesRequired: true,
      rabiesCount: 2,
      rabiesWaitingPeriod: "180 days after second vaccination",
      bloodTest: "FAVN test required",
      healthCertificate: true,
      importPermit: true,
      advanceNotification: "40+ days before arrival"
    },
    
    specialRequirements: [
      "Minimum 7 months preparation time",
      "Two rabies vaccinations required",
      "FAVN blood test at approved lab",
      "180-day waiting period AFTER blood test",
      "Advance notification to Animal Quarantine Service",
      "Only certain airports accept pets"
    ],
    
    entryPoints: ["Narita, Haneda, Kansai, Chubu, New Chitose, Fukuoka"],
    
    commonIssues: [
      "Insufficient preparation time",
      "Blood test timing errors",
      "Wrong microchip format",
      "Missing advance notification"
    ],
    
    seoTitle: "Japan Pet Import 2026 | 180-Day Process & Requirements",
    seoDescription: "Complete Japan pet import guide. 180-day waiting period, FAVN test, double rabies vaccination. Start 7+ months before travel.",
    seoKeywords: ["Japan pet import", "bring dog to Japan", "Japan pet quarantine", "Japan FAVN test", "Japan 180 day rule"]
  },

  "singapore": {
    slug: "singapore",
    name: "Singapore",
    code: "SG",
    flag: "🇸🇬",
    
    importRules: {
      quarantine: "Possible",
      quarantineDays: "0-30 days depending on category",
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "Varies by origin",
      bloodTest: "Required from some countries",
      healthCertificate: true,
      importPermit: true,
      licensingRequired: true
    },
    
    countryCategories: {
      categoryA: ["Australia, New Zealand, UK, Ireland - shortest process"],
      categoryB: ["USA, Canada, most EU - medium requirements"],
      categoryC: ["High-risk rabies countries - strictest requirements"]
    },
    
    specialRequirements: [
      "AVS import license required",
      "Country category determines requirements",
      "Some breeds restricted (Pit Bull, etc.)",
      "HDB flats: only approved small breeds",
      "Private property: most dogs allowed with license"
    ],
    
    seoTitle: "Singapore Pet Import 2026 | AVS License & Category System",
    seoDescription: "Importing pets to Singapore guide. AVS licensing, country categories, quarantine rules, HDB breed restrictions. Complete requirements.",
    seoKeywords: ["Singapore pet import", "bring dog to Singapore", "Singapore AVS license", "Singapore pet quarantine", "Singapore approved breeds"]
  },

  "uae": {
    slug: "uae",
    name: "United Arab Emirates",
    code: "AE",
    flag: "🇦🇪",
    
    importRules: {
      quarantine: false,
      quarantineDays: 0,
      microchipRequired: true,
      rabiesRequired: true,
      rabiesWaitingPeriod: "21 days",
      additionalVaccines: ["DHPP for dogs", "FVRCP for cats"],
      healthCertificate: true,
      healthCertificateValidity: "10 days",
      importPermit: true,
      bloodTest: "Rabies titer test for some countries"
    },
    
    specialRequirements: [
      "Import permit from MOCCAE",
      "Different requirements for each emirate",
      "Some breeds restricted in certain emirates",
      "Pet must arrive within 10 days of health certificate"
    ],
    
    seoTitle: "UAE Pet Import 2026 | Dubai & Abu Dhabi Requirements",
    seoDescription: "Bringing pets to UAE/Dubai. Import permit, vaccination requirements, no quarantine. Guide for each emirate.",
    seoKeywords: ["UAE pet import", "Dubai pet import", "bring dog to Dubai", "UAE pet permit", "Abu Dhabi pet rules"]
  }
};

// ==================== BREEDS DATA ====================
export const breedsData = {
  // BRACHYCEPHALIC (SNUB-NOSED) BREEDS - HIGH RISK
  "french-bulldog": {
    slug: "french-bulldog",
    name: "French Bulldog",
    type: "dog",
    category: "brachycephalic",
    riskLevel: "HIGH",
    
    physicalTraits: {
      weight: "16-28 lbs (7-13 kg)",
      height: "11-13 inches",
      lifespan: "10-12 years"
    },
    
    travelRisks: {
      respiratory: "Very High - severely shortened airways",
      heatStroke: "Very High - cannot regulate temperature effectively",
      stress: "High - breathing difficulties worsen under stress",
      altitude: "Moderate - cabin pressure affects breathing"
    },
    
    airlineRestrictions: {
      bannedFromCargo: ["Delta", "United", "American", "Air France", "Lufthansa", "British Airways", "Emirates", "Most major airlines"],
      cabinAllowed: ["Most airlines if under weight limit"],
      notes: "French Bulldogs are the #1 breed rejected from cargo holds due to multiple in-flight deaths"
    },
    
    recommendedCrateSize: {
      length: "24-28 inches",
      width: "18-20 inches",
      height: "21-24 inches",
      notes: "Extra ventilation crucial. Never use cargo for this breed."
    },
    
    travelTips: [
      "ONLY travel in cabin - never cargo",
      "Choose early morning or late evening flights",
      "Avoid summer travel entirely",
      "Bring portable fan and cooling mat",
      "Keep water available at all times",
      "Inform airline in advance",
      "Consider sedation consultation with vet"
    ],
    
    deathStatistics: "French Bulldogs account for the highest number of airline pet deaths. Multiple airlines have banned them from cargo after fatalities.",
    
    seoTitle: "French Bulldog Air Travel 2026 | Airlines, Risks & Safety Guide",
    seoDescription: "Flying with a French Bulldog: which airlines allow them, cargo bans, health risks, safety tips. Many airlines refuse Frenchies - know before you book.",
    seoKeywords: ["French Bulldog flying", "French Bulldog airline", "Frenchie air travel", "French Bulldog cargo ban", "snub nose dog flying"]
  },

  "english-bulldog": {
    slug: "english-bulldog",
    name: "English Bulldog",
    type: "dog",
    category: "brachycephalic",
    riskLevel: "VERY HIGH",
    
    physicalTraits: {
      weight: "40-50 lbs (18-23 kg)",
      height: "14-15 inches",
      lifespan: "8-10 years"
    },
    
    travelRisks: {
      respiratory: "Extreme - most compromised airways of all breeds",
      heatStroke: "Extreme",
      stress: "Very High",
      altitude: "High"
    },
    
    airlineRestrictions: {
      bannedFromCargo: ["All major airlines"],
      cabinAllowed: ["Too large for cabin on most airlines"],
      notes: "English Bulldogs face the most restrictions. Many cannot fly at all due to size + breathing issues."
    },
    
    alternativeTransport: [
      "Ground transport services",
      "Pet relocation specialists",
      "Driving",
      "Ferry services"
    ],
    
    seoTitle: "English Bulldog Air Travel 2026 | BANNED from Most Airlines",
    seoDescription: "English Bulldogs face severe flight restrictions. Most airlines ban them from cargo, too large for cabin. Alternative transport options.",
    seoKeywords: ["English Bulldog flying", "Bulldog airline ban", "Bulldog air travel", "English Bulldog cargo", "Bulldog transport"]
  },

  "pug": {
    slug: "pug",
    name: "Pug",
    type: "dog",
    category: "brachycephalic",
    riskLevel: "HIGH",
    
    physicalTraits: {
      weight: "14-18 lbs (6-8 kg)",
      height: "10-13 inches",
      lifespan: "13-15 years"
    },
    
    travelRisks: {
      respiratory: "High",
      heatStroke: "High",
      stress: "Moderate-High",
      altitude: "Moderate"
    },
    
    airlineRestrictions: {
      bannedFromCargo: ["Most major airlines"],
      cabinAllowed: ["Yes - usually under weight limit"],
      notes: "Pugs can typically fly in cabin due to small size, but cargo is almost universally banned."
    },
    
    recommendedCrateSize: {
      length: "24 inches",
      width: "16-18 inches",
      height: "16-19 inches"
    },
    
    travelTips: [
      "Cabin only - never cargo",
      "Soft-sided carrier with extra ventilation",
      "Ice packs in carrier",
      "Avoid peak summer",
      "Early/late flights only"
    ],
    
    seoTitle: "Pug Air Travel Guide 2026 | Safe Flying Tips & Airline Rules",
    seoDescription: "Flying with a Pug safely. Cabin travel tips, airline policies, health risks, recommended carriers. Keep your Pug safe in the air.",
    seoKeywords: ["Pug flying", "Pug air travel", "Pug airline", "flying with Pug", "Pug cargo ban"]
  },

  "golden-retriever": {
    slug: "golden-retriever",
    name: "Golden Retriever",
    type: "dog",
    category: "standard",
    riskLevel: "LOW",
    
    physicalTraits: {
      weight: "55-75 lbs (25-34 kg)",
      height: "21-24 inches",
      lifespan: "10-12 years"
    },
    
    travelRisks: {
      respiratory: "Low",
      heatStroke: "Moderate (thick coat)",
      stress: "Low - generally calm travelers",
      altitude: "Low"
    },
    
    airlineRestrictions: {
      bannedFromCargo: [],
      cabinAllowed: ["No - too large"],
      notes: "Golden Retrievers are excellent cargo travelers. Most airlines accept them without restrictions."
    },
    
    recommendedCrateSize: {
      length: "42 inches",
      width: "28-30 inches",
      height: "30-32 inches",
      IATASize: "500 or 700 series"
    },
    
    travelTips: [
      "Use large IATA-approved crate",
      "Freeze water bowl before flight",
      "Include familiar blanket",
      "Exercise well before travel",
      "No food 4-6 hours before flight"
    ],
    
    seoTitle: "Golden Retriever Air Travel 2026 | Crate Size & Airline Guide",
    seoDescription: "Flying with a Golden Retriever. Recommended crate sizes, airline policies, cargo tips. Goldens are excellent travelers - here's how to prepare.",
    seoKeywords: ["Golden Retriever flying", "Golden Retriever crate size", "Golden Retriever air travel", "flying with Golden Retriever"]
  },

  "labrador-retriever": {
    slug: "labrador-retriever",
    name: "Labrador Retriever",
    type: "dog",
    category: "standard",
    riskLevel: "LOW",
    
    physicalTraits: {
      weight: "55-80 lbs (25-36 kg)",
      height: "21-24 inches",
      lifespan: "10-14 years"
    },
    
    travelRisks: {
      respiratory: "Low",
      heatStroke: "Moderate",
      stress: "Low",
      altitude: "Low"
    },
    
    airlineRestrictions: {
      bannedFromCargo: [],
      cabinAllowed: ["No - too large"],
      notes: "Labs are among the best flying dogs. No major airline restrictions."
    },
    
    recommendedCrateSize: {
      length: "42 inches",
      width: "28-30 inches", 
      height: "30-32 inches",
      IATASize: "500 or 700 series"
    },
    
    seoTitle: "Labrador Retriever Air Travel 2026 | Complete Flying Guide",
    seoDescription: "Flying with a Labrador Retriever. Crate recommendations, airline acceptance, preparation tips. Labs are great flyers.",
    seoKeywords: ["Labrador Retriever flying", "Lab air travel", "Labrador crate size", "flying with Lab"]
  },

  "german-shepherd": {
    slug: "german-shepherd",
    name: "German Shepherd",
    type: "dog",
    category: "large",
    riskLevel: "LOW-MODERATE",
    
    physicalTraits: {
      weight: "50-90 lbs (22-40 kg)",
      height: "22-26 inches",
      lifespan: "9-13 years"
    },
    
    travelRisks: {
      respiratory: "Low",
      heatStroke: "Moderate",
      stress: "Moderate - can be anxious",
      altitude: "Low"
    },
    
    airlineRestrictions: {
      bannedFromCargo: [],
      cabinAllowed: ["No - too large"],
      restrictions: ["Some countries have breed restrictions (UK, Australia restrictions on import of certain lines)"]
    },
    
    recommendedCrateSize: {
      length: "48 inches",
      width: "32 inches",
      height: "35 inches",
      IATASize: "700 series"
    },
    
    travelTips: [
      "Familiarize dog with crate weeks before",
      "Consider calming aids",
      "Large, sturdy crate essential",
      "Check destination country breed rules"
    ],
    
    seoTitle: "German Shepherd Air Travel 2026 | Crate Size & International Rules",
    seoDescription: "Flying with a German Shepherd. Crate requirements, airline policies, country restrictions. GSDs need careful preparation for air travel.",
    seoKeywords: ["German Shepherd flying", "German Shepherd crate", "GSD air travel", "flying with German Shepherd"]
  },

  "persian-cat": {
    slug: "persian-cat",
    name: "Persian Cat",
    type: "cat",
    category: "brachycephalic",
    riskLevel: "MODERATE-HIGH",
    
    physicalTraits: {
      weight: "7-12 lbs (3-5.5 kg)",
      lifespan: "10-17 years"
    },
    
    travelRisks: {
      respiratory: "Moderate-High (flat face)",
      heatStroke: "Moderate",
      stress: "Moderate",
      altitude: "Moderate"
    },
    
    airlineRestrictions: {
      bannedFromCargo: ["Some airlines restrict flat-faced cats"],
      cabinAllowed: ["Yes - most airlines"],
      notes: "Persian cats face some restrictions due to flat face. Cabin travel strongly recommended."
    },
    
    seoTitle: "Persian Cat Air Travel 2026 | Airline Rules & Safety Tips",
    seoDescription: "Flying with a Persian cat. Flat-faced cat airline restrictions, cabin travel tips, health considerations. Keep your Persian safe.",
    seoKeywords: ["Persian cat flying", "Persian cat airline", "flat faced cat travel", "Persian cat cargo"]
  }
};

// ==================== GUIDES/ARTICLES DATA ====================
export const guidesData = {
  "iata-crate-requirements-2026": {
    slug: "iata-crate-requirements-2026",
    title: "IATA Pet Crate Requirements: The Full Checklist",
    category: "crate-compliance",
    readTime: "5 min",
    introduction: "IATA publishes the container requirements that airlines following IATA rules use for dogs and cats travelling in the hold. Here is every rule in plain English, with the official source.",
    sections: [
      { title: "1. Size: the IATA formula", content: `**Length** = A + ½ B (A = nose tip to root of tail; B = floor to elbow joint)

**Width** = C × 2 (C = width across shoulders or widest point). 2 animals: C × 3. 3 animals: C × 4.

**Height** = D + bedding (D = standing height, top of head or ear tip)

**Snub-nosed breeds**: the container must be 10% larger.

These are internal dimensions. Use the free calculator to work them out.` },
      { title: "2. Fasteners: bolts, not clips only", content: `The top and bottom halves of a plastic container must be bolted together. Plastic clips alone are not sufficient. Metal bolts are recommended in every hole.` },
      { title: "3. Ventilation on all four sides", content: `Ventilation openings are required on all four sides, with a total ventilated area of at least 16% of the surface of the four sides. Openings on the sides and back are placed over the upper two-thirds of the container.` },
      { title: "4. Door, bowls, wheels, floor, labels", content: `• The door forms the whole of one end of the container
• Food and water containers are fixed inside and can be refilled from outside
• Wheels are removed or rendered inoperable
• The floor is solid and leak-proof, with absorbent bedding
• A "Live Animals" label and "This Way Up" labels are applied

Source: IATA Pet Container Requirements (https://www.iata.org/contentassets/b0016da92c86449f850fe9560827bbea/pet-container-requirements.pdf)` }
    ],
    seoTitle: "IATA Crate Requirements 2026: The Full Checklist",
    seoDescription: "Size formula, bolts, ventilation, door, bowls, labels, wheels and floor: every IATA pet crate rule explained in plain English, with a free calculator.",
    seoKeywords: ["IATA crate requirements", "IATA pet container requirements", "airline crate rules"]
  },

  "why-airlines-reject-crates": {
    slug: "why-airlines-reject-crates",
    title: "Why Airlines Reject Pet Crates: The Top 10 Reasons",
    category: "crate-compliance",
    readTime: "8 min",
    lastUpdated: "2026",
    
    introduction: "Every day, pet owners arrive at airport check-in counters only to face the devastating news: their pet's crate has been rejected. The flight leaves in hours, and there's no quick fix. Understanding why airlines reject crates - and how to prevent it - can save you from this nightmare scenario.",
    
    sections: [
      {
        title: "1. Crate Too Small",
        content: `A crate that doesn't meet IATA sizing requirements will not be accepted. Your pet must be able to:
        
• STAND upright without touching the ceiling
• TURN AROUND in a complete 360° circle
• LIE DOWN with legs fully extended

The IATA formula (minimum internal dimensions):
- Length = A + ½ B (A = nose tip to root of tail; B = floor to elbow joint)
- Width = C × 2 (C = width across shoulders or widest point); 2 animals: C × 3; 3 animals: C × 4
- Height = D + bedding (D = standing height, top of head or ear tip)
- Snub-nosed (brachycephalic) breeds: container 10% larger

Source: IATA Pet Container Requirements (https://www.iata.org/contentassets/b0016da92c86449f850fe9560827bbea/pet-container-requirements.pdf)`,
        image: null
      },
      {
        title: "2. Plastic Clips Instead of Metal Bolts",
        content: `Many crates come with plastic clips securing the top and bottom shell. THIS IS NOT ACCEPTABLE FOR CARGO.

IATA requires the top and bottom halves of plastic containers to be bolted together. Plastic clips alone are not sufficient. Metal bolts are recommended in every hole.

SOLUTION: Replace or supplement the plastic clips with metal nuts and bolts through the existing holes.

Source: IATA Pet Container Requirements (https://www.iata.org/contentassets/b0016da92c86449f850fe9560827bbea/pet-container-requirements.pdf)`,
        image: null
      },
      {
        title: "3. Soft-Sided Carrier for Cargo",
        content: `Soft-sided carriers are ONLY acceptable for cabin travel. Using a fabric carrier for checked baggage/cargo will result in immediate rejection.

CARGO REQUIRES:
• Rigid plastic (Vari Kennel, Sky Kennel)
• Fiberglass
• Metal
• Wood (for large animals)

The container must protect the animal if other cargo shifts during turbulence.`,
        image: null
      },
      {
        title: "4. Inadequate Ventilation",
        content: `IATA requires ventilation openings on ALL FOUR sides of the container, with a total ventilated area of at least 16% of the surface of the four sides. Openings on the sides and back are placed over the upper two-thirds of the container.

CHECK FOR:
• Ventilation openings on the front, back, left and right
• No blocked ventilation (stickers, tape covering holes)

Source: IATA Pet Container Requirements (https://www.iata.org/contentassets/b0016da92c86449f850fe9560827bbea/pet-container-requirements.pdf)`,
        image: null
      },
      {
        title: "5. Broken or Insecure Door",
        content: `The door is the most critical security feature. Rejection triggers include:

• Bent hinges
• Latch doesn't close securely
• Door swings open under pressure
• Top-opening doors (not allowed - must open outward)
• Missing secondary locking mechanism

TEST: Can you lift the crate by the door without it opening? If not, it's not secure enough.`,
        image: null
      },
      {
        title: "6. No Food/Water Bowls",
        content: `For flights over 4 hours (or any international flight), attached food and water bowls are REQUIRED.

Requirements:
• Bowls must be accessible from OUTSIDE the crate
• Attached to door or interior wall
• Water bowl should be deep enough to not spill immediately

Many airlines provide bowls at check-in, but don't count on it. Bring your own.`,
        image: null
      },
      {
        title: "7. Missing Labels and Documentation",
        content: `Required labels that MUST be on the crate:

• "LIVE ANIMAL" stickers (green, IATA standard)
• "THIS WAY UP" arrows on all sides
• Your contact information
• Feeding and watering instructions
• Health certificate attached in waterproof sleeve

Missing labels = delayed boarding while you scramble to find a pen.`,
        image: null
      },
      {
        title: "8. Wheels Still Attached",
        content: `If your crate has wheels, they MUST be removed or immobilized before check-in.

Why? Rolling crates can:
• Shift during flight, crushing other cargo
• Roll into aircraft walls
• Create safety hazards for handlers

Remove wheels completely or secure them with heavy tape.`,
        image: null
      },
      {
        title: "9. Absorbent Bedding Missing or Inadequate",
        content: `The crate floor needs:

• Absorbent material (puppy pads, newspaper, towels)
• Waterproof bottom layer
• Comfortable bedding for long flights

Bare plastic floors are rejected because:
• No absorption of urine/spills
• Slippery surface dangerous for pet
• No comfort for extended travel`,
        image: null
      },
      {
        title: "10. Brachycephalic Breed in Cargo",
        content: `Even with a perfect crate, if you're flying a snub-nosed breed in cargo, you'll be rejected.

BANNED FROM CARGO (Most Airlines):
• French Bulldog, English Bulldog
• Pug, Boston Terrier
• Boxer, Shih Tzu
• Pekingese, Cavalier King Charles
• Persian cats, Himalayan cats

These breeds have respiratory issues that make cargo holds dangerous. Cabin only.`,
        image: null
      }
    ],
    
    conclusion: "Check your crate against the IATA requirements before you get to the airport: size formula, bolts, ventilation on four sides, door, bowls. PetFlyCheck is an independent service, not affiliated with IATA or any airline.",
    
    callToAction: {
      text: "Check Your Crate Now",
      url: "/"
    },
    
    seoTitle: "Why Airlines Reject Pet Crates: 10 Reasons & How to Avoid Them",
    seoDescription: "Wrong size, plastic clips only, ventilation missing on a side: the crate problems that IATA rules flag, and how to fix each one before check-in.",
    seoKeywords: ["airline pet crate rejection", "IATA crate requirements", "pet crate refused", "airline pet travel problems", "crate too small airline"]
  },

  "iata-crate-size-calculator": {
    slug: "iata-crate-size-calculator",
    title: "IATA crate size calculator for dogs and cats",
    category: "crate-compliance",
    readTime: "3 min",
    introduction: "Airlines that follow IATA rules require a crate big enough for your pet to stand, turn around and lie down naturally. IATA sets this out as a simple formula based on four measurements of your pet. Enter them below to see the minimum inner dimensions your crate needs. It's free, and nothing is sent to us unless you choose to continue to a full check. PetFlyCheck is independent and not affiliated with IATA.",
    sections: [
      { title: "Should I measure the inside or the outside of the crate?", content: "The inside. IATA's formula gives the minimum inner space your pet needs. Outside dimensions include the walls and are always larger." },
      { title: "My crate is a little smaller than the result. Is that a problem?", content: "It can be. The formula gives IATA's minimum, and airlines can refuse a crate that doesn't meet it. If you're close, re-measure your pet standing naturally. If it's still under, a larger crate is the safer choice." },
      { title: "Why does my snub-nosed dog or cat need a bigger crate?", content: "IATA asks for a crate 10% larger for snub-nosed breeds, to give them more room and air during travel." },
      { title: "Is this the size my airline requires?", content: "It's the IATA minimum, which airlines following IATA rules use as a baseline. Some airlines add their own limits, such as maximum crate sizes for certain aircraft. Check with your airline, or choose our Full Audit, which includes airline-specific rules." },
      { title: "Is the size the only thing that matters?", content: "No. IATA also sets rules for fasteners, ventilation on all four sides, the door and latch, bowls, labels, wheels and the floor. Our $39 Crate Check reviews these from three photos." }
    ],
    faq: true,
    seoTitle: "IATA Crate Size Calculator for Dogs & Cats — Free Tool",
    seoDescription: "Work out the minimum crate size your dog or cat needs to fly, using the official IATA formula. Free, instant, in cm or inches. Includes how to measure.",
    seoKeywords: ["IATA crate size calculator", "pet crate measurements", "dog crate dimensions IATA"]
  },

  "metal-bolts-vs-plastic-clips": {
    slug: "metal-bolts-vs-plastic-clips",
    title: "Metal Bolts vs Plastic Clips: Why Your Crate Will Be Rejected",
    category: "crate-compliance",
    readTime: "5 min",
    lastUpdated: "2026",
    
    introduction: "You bought an expensive 'airline approved' crate. You measured your pet correctly. You have all the documents. But for cargo, IATA requires the two halves of a plastic crate to be bolted together: plastic clips alone are not sufficient.",
    
    sections: [
      {
        title: "The Plastic Clip Problem",
        content: `Many plastic pet crates come with PLASTIC CLIPS securing the top and bottom shell.

IATA Pet Container Requirements state that the top and bottom halves of plastic containers must be bolted together, and that plastic clips alone are not sufficient. Metal bolts are recommended in every hole.

Source: IATA Pet Container Requirements (https://www.iata.org/contentassets/b0016da92c86449f850fe9560827bbea/pet-container-requirements.pdf)`,
        image: null
      },
      {
        title: "What Airlines Actually Require",
        content: `For cargo transport, IATA requires:

**ACCEPTABLE:**
✅ Metal nuts and bolts through the existing holes, joining the top and bottom halves

**NOT SUFFICIENT ON THEIR OWN:**
❌ Plastic clips
❌ Tape

Your airline may add its own requirements (for example cable ties). Check its official policy.

Source: IATA Pet Container Requirements (https://www.iata.org/contentassets/b0016da92c86449f850fe9560827bbea/pet-container-requirements.pdf)`,
        image: null
      },
      {
        title: "How to Fix Your Crate",
        content: `You don't need to buy a new crate. Here's how to upgrade:

**WHAT YOU'LL NEED:**
• 8 metal bolts (size to fit existing holes)
• 8 metal nuts with washers
• 8 zip ties (as backup)
• Drill if holes need enlarging

**STEP BY STEP:**
1. Remove plastic clips
2. Line up top and bottom shells
3. Insert bolt through existing holes
4. Add washer and nut on inside
5. Tighten firmly (not overtight - don't crack plastic)
6. Add zip tie through same holes as backup
7. Repeat for all four corners

Check the airline's official policy for any additional fastening requirement.`,
        image: null
      },
      {
        title: "Visual Inspection at Check-In",
        content: `When the airline agent inspects your crate, they will:

1. **Check ALL four corners** for secure fastening
2. **Pull on the shell** to test if it separates
3. **Verify metal hardware** (they know plastic from metal)
4. **Ensure clips aren't just hidden** under tape

If they see plastic clips - or if the shells move when pulled - your crate fails.`,
        image: null
      }
    ],
    
    seoTitle: "Metal Bolts vs Plastic Clips for Pet Crates | Why You'll Be Rejected",
    seoDescription: "IATA: the two halves of a plastic pet crate must be bolted together; plastic clips alone are not sufficient. How to fit metal bolts before you fly.",
    seoKeywords: ["pet crate metal bolts", "airline crate plastic clips", "IATA crate bolts", "pet crate rejected", "crate clips vs bolts"]
  },

  "pet-travel-checklist": {
    slug: "pet-travel-checklist",
    title: "Ultimate Pet Air Travel Checklist: 30 Days to Flight Day",
    category: "preparation",
    readTime: "10 min",
    lastUpdated: "2026",
    
    introduction: "Flying with your pet requires careful preparation. Start too late, and you risk missing vaccinations deadlines, document requirements, or crate modifications. This checklist breaks down exactly what to do, and when.",
    
    sections: [
      {
        title: "30 Days Before Flight",
        content: `**DOCUMENTATION:**
□ Check destination country's pet import requirements
□ Verify airline's specific pet policy
□ Book vet appointment for health certificate (schedule for 5-7 days before flight)
□ Confirm rabies vaccination is current (21+ days before travel for most destinations)
□ Order any required import permits

**CRATE PREPARATION:**
□ Purchase IATA-compliant crate (if needed)
□ Measure your pet using IATA formula
□ Replace plastic clips with metal bolts
□ Start crate training (pet should sleep in crate)

**BOOKING:**
□ Call airline to add pet to reservation
□ Confirm pet is on booking (cargo vs cabin)
□ Note weight and crate dimension requirements`,
        image: null
      },
      {
        title: "14 Days Before Flight",
        content: `**CRATE TRAINING:**
□ Pet should be comfortable spending 4+ hours in crate
□ Practice closing door with pet inside
□ Simulate car rides to airport in crate
□ Ensure pet has eaten and eliminated in crate environment

**HEALTH CHECK:**
□ Confirm vet appointment is scheduled
□ Prepare list of questions for vet
□ Get microchip number documented
□ Research if destination requires specific vaccines

**SUPPLIES:**
□ Purchase attached food/water bowls
□ Buy absorbent pads for crate floor
□ Get "Live Animal" stickers and "This Way Up" arrows
□ Prepare waterproof document sleeve`,
        image: null
      },
      {
        title: "7-10 Days Before Flight",
        content: `**VET VISIT:**
□ Get official health certificate (MUST be within 10 days of travel)
□ Verify rabies certificate is attached
□ Ask about sedation (most airlines prohibit it)
□ Get any required destination-specific treatments
□ Have vet sign all documents clearly

**CRATE FINAL CHECK:**
□ Top and bottom halves bolted together (metal bolts, not clips only)
□ Door closes and latches securely
□ Ventilation openings on all four sides, not blocked
□ Bowls attached and accessible from outside
□ Absorbent bedding in place
□ Pet fits comfortably (stand, turn, lie down)`,
        image: null
      },
      {
        title: "3 Days Before Flight",
        content: `**DOCUMENTATION REVIEW:**
□ Health certificate dated correctly (within 10 days)
□ All signatures present
□ Microchip number matches documents
□ Import permits printed (if required)
□ Airline confirmation shows pet

**CRATE LABELING:**
□ "Live Animals" label and "This Way Up" labels
□ "This Way Up" arrows pointing correct direction
□ Your name, phone, email on crate
□ Destination address on crate
□ Feeding instructions attached

**PET PREP:**
□ Begin reducing food portions
□ Increase water intake
□ Final grooming (clean pet travels better)
□ Clip nails to prevent catching on crate`,
        image: null
      },
      {
        title: "Day Before Flight",
        content: `**FINAL PREPARATIONS:**
□ Pack pet travel bag (documents, treats, cleanup supplies)
□ Freeze water in bowl (melts slowly, won't spill at takeoff)
□ Light meal 6 hours before flight time
□ Final potty break schedule planned
□ Crate loaded in vehicle
□ Airline check-in time confirmed (usually 2-3 hours early for pets)

**DOCUMENT PACKET:**
□ Health certificate (original)
□ Rabies certificate (original)  
□ Vaccination records (copies)
□ Microchip documentation
□ Import permits (if required)
□ Airline booking confirmation
□ Your ID/passport`,
        image: null
      },
      {
        title: "Flight Day",
        content: `**MORNING:**
□ No food 4-6 hours before flight
□ Water available until 2 hours before
□ Long walk/exercise to tire pet
□ Final potty break at home
□ Potty break at airport (pet relief area)

**AT AIRPORT:**
□ Arrive 3 hours before (international) or 2 hours (domestic)
□ Check in at cargo facility (if cargo) or main counter (cabin)
□ Present all documents
□ Allow inspection of pet and crate
□ Ask for tracking info (cargo)
□ Confirm connecting flight arrangements (if any)

**BEFORE BOARDING:**
□ Say goodbye calmly (pets sense stress)
□ Confirm pet is loaded (ask gate agent)
□ Keep documents accessible for arrival`,
        image: null
      }
    ],
    
    seoTitle: "Pet Air Travel Checklist 2026 | 30-Day Preparation Guide",
    seoDescription: "Complete day-by-day checklist for flying with your pet. Documents, crate prep, vet visits, airport procedures. Start 30 days before your flight.",
    seoKeywords: ["pet travel checklist", "flying with pet checklist", "pet air travel preparation", "dog flight checklist", "cat air travel guide"]
  }
};

export default { airlinesData, countriesData, breedsData, guidesData };
